import { createTheme, Theme } from '@mui/material'

// Tema de CareMe a partir de los tokens del diseno (Penpot / shadcn).
//
// Los tokens llegaron en oklch(). MUI NO sabe leer ese formato: decomposeColor
// solo acepta rgb/rgba/hsl/hsla/color() y lanza "Unsupported color" en cuanto
// createTheme calcula las variantes light/dark de un color. Por eso aqui van en
// hex, que es el equivalente exacto. Los mismos valores estan como variables CSS
// en index.css, para lo que se estila por CSS Modules.
export const TOKENS = {
  background: '#ffffff',
  foreground: '#5b682a',
  card: '#ffffff',
  cardForeground: '#5b682a',
  popover: '#ffffff',
  popoverForeground: '#404040',
  primary: '#dae1bc',
  primaryForeground: '#404040',
  secondary: '#5b682a',
  secondaryForeground: '#f3f0f0',
  muted: '#f3f0f0',
  mutedForeground: '#404040',
  accent: '#f3f0f0',
  accentForeground: '#5b682a',
  destructive: '#e14c54',
  destructiveForeground: '#ffffff',
  border: '#f3f0f0',
  input: '#f3f0f0',
  ring: '#f3f0f0',
  sidebar: '#f3f0f0',
  sidebarForeground: '#404040',
  sidebarPrimary: '#dae1bc',
  sidebarPrimaryForeground: '#404040',
  sidebarAccent: '#e4e2e1',
  sidebarAccentForeground: '#5b682a',
  sidebarBorder: '#f3f0f0',
  sidebarRing: '#f3f0f0',
  chart: {
    1: '#bfce83',
    2: '#a3b26a',
    3: '#899752',
    4: '#707d3b',
    5: '#576425',
  },
} as const

export const FONT_SANS = 'Montserrat, ui-sans-serif, sans-serif, system-ui'
export const FONT_SERIF = 'Lora, ui-serif, serif'
export const FONT_MONO = "'Source Code Pro', ui-monospace, monospace"

// --radius: 1.525rem del diseno, en px porque shape.borderRadius de MUI no
// entiende rem.
export const RADIUS = 24.4

// MUI exige exactamente 25 niveles de sombra. El diseno define 11; el resto se
// rellena repitiendo la mayor en vez de 'none', porque los componentes altos
// (Dialog usa elevation 24, Menu 8) se quedarian planos.
const DESIGN_SHADOWS = [
  'none',
  '0 1px 3px 0 rgb(0 0 0 / 0.05)',
  '0 1px 3px 0 rgb(0 0 0 / 0.05)',
  '0 1px 3px 0 rgb(0 0 0 / 0.10), 0 1px 2px -1px rgb(0 0 0 / 0.10)',
  '0 1px 3px 0 rgb(0 0 0 / 0.10), 0 1px 2px -1px rgb(0 0 0 / 0.10)',
  '0 1px 3px 0 rgb(0 0 0 / 0.10), 0 2px 4px -1px rgb(0 0 0 / 0.10)',
  '0 1px 3px 0 rgb(0 0 0 / 0.10), 0 4px 6px -1px rgb(0 0 0 / 0.10)',
  '0 1px 3px 0 rgb(0 0 0 / 0.10), 0 8px 10px -1px rgb(0 0 0 / 0.10)',
  '0 1px 3px 0 rgb(0 0 0 / 0.10), 0 10px 15px -3px rgb(0 0 0 / 0.10)',
  '0 1px 3px 0 rgb(0 0 0 / 0.10), 0 20px 25px -5px rgb(0 0 0 / 0.10)',
  '0 1px 3px 0 rgb(0 0 0 / 0.10), 0 25px 50px -12px rgb(0 0 0 / 0.25)',
]
const shadows = [
  ...DESIGN_SHADOWS,
  ...Array(25 - DESIGN_SHADOWS.length).fill(DESIGN_SHADOWS[DESIGN_SHADOWS.length - 1]),
] as Theme['shadows']

// MUI no conoce estas claves, asi que hay que declararlas o TypeScript rechaza
// el tema al compilar.
declare module '@mui/material/styles' {
  interface Palette {
    custom: typeof TOKENS
  }
  interface PaletteOptions {
    custom?: typeof TOKENS
  }
  interface TypographyVariants {
    fontFamilySerif: string
    fontFamilyMono: string
  }
  interface TypographyVariantsOptions {
    fontFamilySerif?: string
    fontFamilyMono?: string
  }
}

export const caremeTheme = createTheme({
  palette: {
    mode: 'light',

    background: { default: TOKENS.background, paper: TOKENS.card },
    text: { primary: TOKENS.foreground, secondary: TOKENS.mutedForeground },

    primary: { main: TOKENS.primary, contrastText: TOKENS.primaryForeground },
    secondary: { main: TOKENS.secondary, contrastText: TOKENS.secondaryForeground },
    error: { main: TOKENS.destructive, contrastText: TOKENS.destructiveForeground },

    divider: TOKENS.border,

    custom: TOKENS,
  },

  typography: {
    fontFamily: FONT_SANS,
    fontFamilySerif: FONT_SERIF,
    fontFamilyMono: FONT_MONO,
  },

  shape: { borderRadius: RADIUS },

  // OJO: spacing se queda en el valor por defecto de MUI (8px).
  // El --spacing: 0.22rem del diseno es la unidad base de las utilidades de
  // Tailwind, no el spacing de MUI. Traducirlo literalmente (spacing: 0.22)
  // dejaria gap={2} en 0,44px y colapsaria el espaciado de toda la app.

  shadows,

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: 0,
          backgroundColor: TOKENS.background,
          color: TOKENS.foreground,
          fontFamily: FONT_SANS,
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: { borderRadius: RADIUS, textTransform: 'none', fontFamily: FONT_SANS },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS,
          backgroundColor: TOKENS.card,
          color: TOKENS.cardForeground,
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.10)',
        },
      },
    },

    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },

    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.input },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.foreground },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.ring },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: { root: { fontFamily: FONT_SANS } },
    },

    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: RADIUS, backgroundColor: TOKENS.popover },
      },
    },

    MuiChip: {
      styleOverrides: { root: { borderRadius: RADIUS, fontFamily: FONT_SANS } },
    },

    // Barra y menu lateral con los tokens tal como los ha mandado diseno.
    // Ojo: los mockups los enseñan en oscuro y estos tokens los dejan claros
    // (sidebar #f3f0f0). Se aplican los tokens, que es lo acordado, y queda
    // pendiente confirmar con diseno cual de los dos manda.
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: TOKENS.background,
          color: TOKENS.foreground,
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.10)',
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: TOKENS.sidebar,
          color: TOKENS.sidebarForeground,
          borderColor: TOKENS.sidebarBorder,
        },
      },
    },

    MuiTooltip: {
      styleOverrides: { tooltip: { borderRadius: 8, fontFamily: FONT_SANS } },
    },

    MuiTabs: {
      styleOverrides: { indicator: { backgroundColor: TOKENS.foreground } },
    },

    MuiLink: {
      styleOverrides: { root: { color: TOKENS.foreground } },
    },
  },
})

export default caremeTheme
